import { useForm } from 'react-hook-form';

import { getDefaultSiteTypeForOrg } from '@gitbook/app-pricing';
import {
    BrandedDialogAside,
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    FieldError,
    Form,
    Heading,
    Image,
    Label,
    Submit,
    TextField,
    TextInput,
    toast,
    useDialogAction,
} from '@gitbook/spine-react';

import { useNavigation } from '../../navigation';
import { apiClient, useCurrentOrganization } from '../../state';
import docsPreviewSrc from './assets/docs-preview.webp';

type CreateSiteFormValues = {
    name: string;
};

export function CreateSiteDialog(props: { organizationId: string }) {
    const { organizationId } = props;

    const [isPending, startDialogAction] = useDialogAction();
    const { navigate } = useNavigation();
    const organization = useCurrentOrganization();

    const form = useForm({
        defaultValues: {
            name: '',
        },
        disabled: isPending,
    });

    const onSubmit = async (data: CreateSiteFormValues) => {
        await startDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                const { data: site } = await apiClient.sites.createSite(organizationId, {
                    title: data.name,
                    type: getDefaultSiteTypeForOrg(organization),
                });

                navigate({
                    screen: {
                        type: 'site',
                        organization: organizationId,
                        site: site.id,
                    },
                });

                // Reset the form to allow the user to create another site
                form.reset();

                close();
            }, 'Error while creating site');
        });
    };

    return (
        <Dialog size={{ width: 724 }}>
            <div className="flex h-full w-full max-md:flex-col">
                <div className="hidden w-full max-w-64 overflow-hidden md:flex">
                    <BrandedDialogAside
                        // Remove the gradient on dark mode
                        backgroundSrc={{
                            dark: '',
                        }}
                    >
                        <Image
                            src={docsPreviewSrc}
                            className="-m-5 h-full flex-1 object-cover"
                            alt="Docs preview"
                        />
                    </BrandedDialogAside>
                </div>
                <div className="flex-1">
                    <DialogHeader>
                        <Heading>Create a new site</Heading>
                        <Description>
                            Give your site a name your visitors will recognize.
                        </Description>
                    </DialogHeader>
                    <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
                        <DialogBody data-testid="site_create.dialog" className="min-h-fit">
                            <TextField name="name" required minLength={2} maxLength={50}>
                                <Label>Site name</Label>
                                <TextInput autoFocus placeholder="Enter site name…" />
                                <FieldError />
                            </TextField>
                        </DialogBody>
                        <DialogFooter>
                            <DialogCloseButton onClick={() => form.reset()} />
                            <Submit label="Create" />
                        </DialogFooter>
                    </Form>
                </div>
            </div>
        </Dialog>
    );
}
